import { useMemo, useState } from 'react';
import { Circle, GoogleMap, InfoWindow, useJsApiLoader } from '@react-google-maps/api';
import { HiDownload } from 'react-icons/hi';
import WidgetWrapper from '../WidgetWrapper';
import { CurrencyFormat } from '../shared/Formats';
import { DashboardP2Insights } from '../../types/dashboard.types';
import config from '../../config';
import { downloadCsv } from '../../utils/csvDownload';

// Campaign.country stores ISO 3166-1 alpha-2 codes (e.g. "PS", "US", "AE").
// Maps ISO2 code → display name. Unknown/missing codes fall back to the raw code.
const ISO2_TO_COUNTRY: Record<string, string> = {
  AF: 'Afghanistan', AL: 'Albania', DZ: 'Algeria', AO: 'Angola',
  AR: 'Argentina', AM: 'Armenia', AU: 'Australia', AT: 'Austria',
  AZ: 'Azerbaijan', BD: 'Bangladesh', BE: 'Belgium', BO: 'Bolivia',
  BA: 'Bosnia & Herzegovina', BR: 'Brazil', BG: 'Bulgaria',
  KH: 'Cambodia', CM: 'Cameroon', CA: 'Canada', CL: 'Chile',
  CN: 'China', CO: 'Colombia', HR: 'Croatia', CZ: 'Czechia',
  DK: 'Denmark', EC: 'Ecuador', EG: 'Egypt', ET: 'Ethiopia',
  FI: 'Finland', FR: 'France', GE: 'Georgia', DE: 'Germany',
  GH: 'Ghana', GR: 'Greece', GT: 'Guatemala', HU: 'Hungary',
  IN: 'India', ID: 'Indonesia', IR: 'Iran', IQ: 'Iraq',
  IE: 'Ireland', IL: 'Israel', IT: 'Italy', JM: 'Jamaica',
  JP: 'Japan', JO: 'Jordan', KZ: 'Kazakhstan', KE: 'Kenya',
  KW: 'Kuwait', LB: 'Lebanon', LY: 'Libya', MY: 'Malaysia',
  MX: 'Mexico', MA: 'Morocco', MZ: 'Mozambique', MM: 'Myanmar',
  NP: 'Nepal', NL: 'Netherlands', NZ: 'New Zealand', NG: 'Nigeria',
  NO: 'Norway', PK: 'Pakistan', PS: 'Palestine', PA: 'Panama',
  PE: 'Peru', PH: 'Philippines', PL: 'Poland', PT: 'Portugal',
  QA: 'Qatar', RO: 'Romania', RU: 'Russia', SA: 'Saudi Arabia',
  SN: 'Senegal', RS: 'Serbia', SG: 'Singapore', SK: 'Slovakia',
  SO: 'Somalia', ZA: 'South Africa', KR: 'South Korea', ES: 'Spain',
  LK: 'Sri Lanka', SD: 'Sudan', SE: 'Sweden', CH: 'Switzerland',
  SY: 'Syria', TW: 'Taiwan', TZ: 'Tanzania', TH: 'Thailand',
  TN: 'Tunisia', TR: 'Turkey', UG: 'Uganda', UA: 'Ukraine',
  AE: 'United Arab Emirates', GB: 'United Kingdom', US: 'United States',
  UY: 'Uruguay', UZ: 'Uzbekistan', VE: 'Venezuela', VN: 'Vietnam',
  YE: 'Yemen', ZM: 'Zambia', ZW: 'Zimbabwe',
};

const ISO2_TO_COORDS: Record<string, { lat: number; lng: number }> = {
  AF: { lat: 33.9391, lng: 67.71 },
  AL: { lat: 41.1533, lng: 20.1683 },
  DZ: { lat: 28.0339, lng: 1.6596 },
  AO: { lat: -11.2027, lng: 17.8739 },
  AR: { lat: -38.4161, lng: -63.6167 },
  AM: { lat: 40.0691, lng: 45.0382 },
  AU: { lat: -25.2744, lng: 133.7751 },
  AT: { lat: 47.5162, lng: 14.5501 },
  AZ: { lat: 40.1431, lng: 47.5769 },
  BD: { lat: 23.685, lng: 90.3563 },
  BE: { lat: 50.5039, lng: 4.4699 },
  BO: { lat: -16.2902, lng: -63.5887 },
  BA: { lat: 43.9159, lng: 17.6791 },
  BR: { lat: -14.235, lng: -51.9253 },
  BG: { lat: 42.7339, lng: 25.4858 },
  KH: { lat: 12.5657, lng: 104.991 },
  CM: { lat: 7.3697, lng: 12.3547 },
  CA: { lat: 56.1304, lng: -106.3468 },
  CL: { lat: -35.6751, lng: -71.543 },
  CN: { lat: 35.8617, lng: 104.1954 },
  CO: { lat: 4.5709, lng: -74.2973 },
  HR: { lat: 45.1, lng: 15.2 },
  CZ: { lat: 49.8175, lng: 15.473 },
  DK: { lat: 56.2639, lng: 9.5018 },
  EC: { lat: -1.8312, lng: -78.1834 },
  EG: { lat: 26.8206, lng: 30.8025 },
  ET: { lat: 9.145, lng: 40.4897 },
  FI: { lat: 61.9241, lng: 25.7482 },
  FR: { lat: 46.2276, lng: 2.2137 },
  GE: { lat: 42.3154, lng: 43.3569 },
  DE: { lat: 51.1657, lng: 10.4515 },
  GH: { lat: 7.9465, lng: -1.0232 },
  GR: { lat: 39.0742, lng: 21.8243 },
  GT: { lat: 15.7835, lng: -90.2308 },
  HU: { lat: 47.1625, lng: 19.5033 },
  IN: { lat: 20.5937, lng: 78.9629 },
  ID: { lat: -0.7893, lng: 113.9213 },
  IR: { lat: 32.4279, lng: 53.688 },
  IQ: { lat: 33.2232, lng: 43.6793 },
  IE: { lat: 53.1424, lng: -7.6921 },
  IL: { lat: 31.0461, lng: 34.8516 },
  IT: { lat: 41.8719, lng: 12.5674 },
  JM: { lat: 18.1096, lng: -77.2975 },
  JP: { lat: 36.2048, lng: 138.2529 },
  JO: { lat: 30.5852, lng: 36.2384 },
  KZ: { lat: 48.0196, lng: 66.9237 },
  KE: { lat: -0.0236, lng: 37.9062 },
  KW: { lat: 29.3117, lng: 47.4818 },
  LB: { lat: 33.8547, lng: 35.8623 },
  LY: { lat: 26.3351, lng: 17.2283 },
  MY: { lat: 4.2105, lng: 101.9758 },
  MX: { lat: 23.6345, lng: -102.5528 },
  MA: { lat: 31.7917, lng: -7.0926 },
  MZ: { lat: -18.6657, lng: 35.5296 },
  MM: { lat: 21.9162, lng: 95.956 },
  NP: { lat: 28.3949, lng: 84.124 },
  NL: { lat: 52.1326, lng: 5.2913 },
  NZ: { lat: -40.9006, lng: 174.886 },
  NG: { lat: 9.082, lng: 8.6753 },
  NO: { lat: 60.472, lng: 8.4689 },
  PK: { lat: 30.3753, lng: 69.3451 },
  PS: { lat: 31.9522, lng: 35.2332 },
  PA: { lat: 8.538, lng: -80.7821 },
  PE: { lat: -9.19, lng: -75.0152 },
  PH: { lat: 12.8797, lng: 121.774 },
  PL: { lat: 51.9194, lng: 19.1451 },
  PT: { lat: 39.3999, lng: -8.2245 },
  QA: { lat: 25.3548, lng: 51.1839 },
  RO: { lat: 45.9432, lng: 24.9668 },
  RU: { lat: 61.524, lng: 105.3188 },
  SA: { lat: 23.8859, lng: 45.0792 },
  SN: { lat: 14.4974, lng: -14.4524 },
  RS: { lat: 44.0165, lng: 21.0059 },
  SG: { lat: 1.3521, lng: 103.8198 },
  SK: { lat: 48.669, lng: 19.699 },
  SO: { lat: 5.1521, lng: 46.1996 },
  ZA: { lat: -30.5595, lng: 22.9375 },
  KR: { lat: 35.9078, lng: 127.7669 },
  ES: { lat: 40.4637, lng: -3.7492 },
  LK: { lat: 7.8731, lng: 80.7718 },
  SD: { lat: 12.8628, lng: 30.2176 },
  SE: { lat: 60.1282, lng: 18.6435 },
  CH: { lat: 46.8182, lng: 8.2275 },
  SY: { lat: 34.8021, lng: 38.9968 },
  TW: { lat: 23.6978, lng: 120.9605 },
  TZ: { lat: -6.369, lng: 34.8888 },
  TH: { lat: 15.87, lng: 100.9925 },
  TN: { lat: 33.8869, lng: 9.5375 },
  TR: { lat: 38.9637, lng: 35.2433 },
  UG: { lat: 1.3733, lng: 32.2903 },
  UA: { lat: 48.3794, lng: 31.1656 },
  AE: { lat: 23.4241, lng: 53.8478 },
  GB: { lat: 55.3781, lng: -3.436 },
  US: { lat: 37.0902, lng: -95.7129 },
  UY: { lat: -32.5228, lng: -55.7658 },
  UZ: { lat: 41.3775, lng: 64.5853 },
  VE: { lat: 6.4238, lng: -66.5897 },
  VN: { lat: 14.0583, lng: 108.2772 },
  YE: { lat: 15.5527, lng: 48.5164 },
  ZM: { lat: -13.1339, lng: 27.8493 },
  ZW: { lat: -19.0154, lng: 29.1549 },
};

const MAP_CONTAINER_STYLE = { width: '100%', height: '320px' };
const DEFAULT_WORLD_CENTER = { lat: 18, lng: 10 };

/** Convert an ISO2 code directly to a flag emoji. Works for any valid ISO2 code. */
function countryFlag(iso2: string): string {
  if (!iso2 || iso2.length !== 2) return '';
  return iso2.toUpperCase().split('')
    .map((c) => String.fromCodePoint(0x1F1E6 + c.charCodeAt(0) - 65))
    .join('');
}

/** Return the display name for an ISO2 code, falling back to the raw code. */
function countryName(iso2: string): string {
  return ISO2_TO_COUNTRY[iso2.toUpperCase()] ?? iso2;
}

type DashboardGeoPerformanceWidgetProps = {
  insights: DashboardP2Insights | null;
  compareInsights?: DashboardP2Insights | null;
  compareLabel?: string;
  loading?: boolean;
};

export default function DashboardGeoPerformanceWidget({
  insights,
  compareInsights = null,
  compareLabel = 'Compare',
  loading = false,
}: DashboardGeoPerformanceWidgetProps) {
  const current = insights?.geoPerformance;
  const compare = compareInsights?.geoPerformance;
  const [viewMode, setViewMode] = useState<'table' | 'map'>('table');
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const { isLoaded: isMapLoaded } = useJsApiLoader({
    googleMapsApiKey: config.googleMapsApiKey,
    id: 'dashboard-geo-performance-map',
    language: 'en',
    region: 'US',
  });

  const mapCountries = useMemo(
    () =>
      (current?.countries ?? [])
        .map((item) => ({
          ...item,
          coords: ISO2_TO_COORDS[item.country.toUpperCase()],
          flag: countryFlag(item.country),
          name: countryName(item.country),
        }))
        .filter((item) => Boolean(item.coords)),
    [current?.countries],
  );

  const selectedCountryData = selectedCountry
    ? mapCountries.find((item) => item.country === selectedCountry) ?? null
    : null;

  const maxDonationAmount = Math.max(...mapCountries.map((item) => item.totalDonationAmount), 0);

  const handleDownload = () => {
    const countries = current?.countries ?? [];
    if (!countries.length) return;
    downloadCsv('geo-performance', countries.map((item) => ({
      Country: countryName(item.country),
      'ISO Code': item.country.toUpperCase(),
      'Donation Amount': item.totalDonationAmount,
      'Bars (Meals)': item.totalBars,
      Donors: item.totalDonors,
      Campaigns: item.campaigns,
    })));
  };

  return (
    <WidgetWrapper
      title="Geo Performance"
      titleStyle="w-full px-4 py-3 rounded-xl bg-primary-10/80 dark:bg-slate-800/80 text-sm uppercase tracking-[0.18em] font-semibold text-gray-700 dark:text-gray-200"
      className="h-full w-full p-4 md:p-5 bg-gradient-to-br from-lime-50/80 via-white/75 to-emerald-100/75 dark:from-lime-900/20 dark:via-slate-900/78 dark:to-emerald-900/18 border-lime-200/50 dark:border-lime-400/15"
    >
      {loading ? (
        <div className="h-64 animate-pulse rounded-2xl border border-white/60 bg-white/60 dark:border-white/10 dark:bg-slate-900/45" />
      ) : (
        <div className="space-y-3 w-full">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <MetricCard label="Countries" value={current?.summary.countries ?? 0} compareValue={compare?.summary.countries} compareLabel={compareLabel} />
            <MetricCard label="Total bars" value={(current?.summary.totalBars ?? 0).toLocaleString()} compareValue={compare ? compare.summary.totalBars.toLocaleString() : undefined} compareLabel={compareLabel} />
            <MetricCard label="Donation amount" value={CurrencyFormat(current?.summary.totalDonationAmount ?? 0)} compareValue={compare ? CurrencyFormat(compare.summary.totalDonationAmount) : undefined} compareLabel={compareLabel} />
          </div>

          <div className="flex items-center justify-between gap-3 rounded-xl border border-white/60 dark:border-white/10 bg-white/60 dark:bg-slate-900/45 px-3 py-2">
            <p className="text-[11px] uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">View</p>
            <div className="flex items-center gap-2">
              <button
                onClick={handleDownload}
                disabled={!(current?.countries?.length)}
                title="Download CSV"
                className="flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-sm font-semibold text-lime-700 transition-colors hover:bg-lime-100 dark:text-lime-300 dark:hover:bg-lime-900/30 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <HiDownload className="h-4 w-4" />
                <span>CSV</span>
              </button>
            <div className="inline-flex rounded-full border border-white/60 dark:border-white/10 bg-white/50 dark:bg-slate-800/60 p-1 backdrop-blur-sm">
              {(['table', 'map'] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setViewMode(mode)}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] transition-colors ${viewMode === mode ? 'bg-white text-gray-900 shadow-sm dark:bg-slate-700 dark:text-white' : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'}`}
                >
                  {mode}
                </button>
              ))}
            </div>            </div>          </div>

          {viewMode === 'table' ? (
            <div className="rounded-2xl border border-white/60 dark:border-white/10 bg-white/60 dark:bg-slate-900/45 overflow-hidden">
              <table className="w-full table-auto border-collapse">
                <thead className="bg-primary-50/80 dark:bg-slate-800/80">
                  <tr>
                    <th className="px-3 py-2 text-left text-[10px] uppercase tracking-[0.16em] font-semibold text-gray-600 dark:text-gray-300">Country</th>
                    <th className="px-3 py-2 text-right text-[10px] uppercase tracking-[0.16em] font-semibold text-gray-600 dark:text-gray-300">Amount</th>
                    <th className="px-3 py-2 text-right text-[10px] uppercase tracking-[0.16em] font-semibold text-gray-600 dark:text-gray-300">Bars</th>
                    <th className="px-3 py-2 text-right text-[10px] uppercase tracking-[0.16em] font-semibold text-gray-600 dark:text-gray-300">Donors</th>
                  </tr>
                </thead>
                <tbody>
                  {(current?.countries ?? []).map((item) => {
                    const flag = countryFlag(item.country);
                    return (
                      <tr key={item.country} className="border-t border-gray-200/60 dark:border-white/10">
                        <td className="px-3 py-2 text-sm font-medium text-gray-800 dark:text-gray-100">
                          <span className="flex items-center gap-1.5">
                            {flag && <span className="text-base leading-none">{flag}</span>}
                            <span>{countryName(item.country)}</span>
                          </span>
                        </td>
                        <td className="px-3 py-2 text-sm text-right font-semibold text-gray-900 dark:text-gray-100">{CurrencyFormat(item.totalDonationAmount)}</td>
                        <td className="px-3 py-2 text-sm text-right text-gray-700 dark:text-gray-300">{item.totalBars.toLocaleString()}</td>
                        <td className="px-3 py-2 text-sm text-right text-gray-700 dark:text-gray-300">{item.totalDonors.toLocaleString()}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="rounded-2xl border border-white/60 dark:border-white/10 bg-white/60 dark:bg-slate-900/45 p-2">
              {!config.googleMapsApiKey ? (
                <div className="h-[320px] rounded-xl border border-dashed border-white/60 dark:border-white/10 bg-white/60 dark:bg-slate-900/45 flex items-center justify-center px-6 text-center text-sm text-gray-500 dark:text-gray-400">
                  Map view needs a Google Maps API key in <span className="mx-1 font-semibold">REACT_APP_GOOGLE_MAPS_API_KEY</span>.
                </div>
              ) : !isMapLoaded ? (
                <div className="h-[320px] animate-pulse rounded-xl border border-white/60 bg-white/60 dark:border-white/10 dark:bg-slate-900/45" />
              ) : mapCountries.length === 0 ? (
                <div className="h-[320px] rounded-xl border border-dashed border-white/60 dark:border-white/10 bg-white/60 dark:bg-slate-900/45 flex items-center justify-center px-6 text-center text-sm text-gray-500 dark:text-gray-400">
                  No mapped countries available for the selected period.
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="overflow-hidden rounded-xl border border-white/60 dark:border-white/10">
                    <GoogleMap
                      mapContainerStyle={MAP_CONTAINER_STYLE}
                      center={DEFAULT_WORLD_CENTER}
                      zoom={1.5}
                      onClick={() => setSelectedCountry(null)}
                      options={{
                        disableDefaultUI: true,
                        zoomControl: true,
                        minZoom: 1,
                        maxZoom: 5,
                        streetViewControl: false,
                        mapTypeControl: false,
                        fullscreenControl: false,
                        styles: [
                          { elementType: 'geometry', stylers: [{ color: '#e5f3eb' }] },
                          { elementType: 'labels.text.fill', stylers: [{ color: '#334155' }] },
                          { elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff' }] },
                          { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#cbd5e1' }] },
                          { featureType: 'landscape.natural', elementType: 'geometry', stylers: [{ color: '#d9f99d' }] },
                          { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#bfdbfe' }] },
                          { featureType: 'poi', stylers: [{ visibility: 'off' }] },
                          { featureType: 'transit', stylers: [{ visibility: 'off' }] },
                        ],
                      }}
                    >
                      {mapCountries.map((item) => {
                        const radius = maxDonationAmount > 0
                          ? 70000 + (item.totalDonationAmount / maxDonationAmount) * 220000
                          : 90000;

                        return (
                          <Circle
                            key={item.country}
                            center={item.coords!}
                            radius={radius}
                            options={{
                              fillColor: '#16a34a',
                              fillOpacity: 0.35,
                              strokeColor: '#15803d',
                              strokeOpacity: 0.85,
                              strokeWeight: 1.5,
                            }}
                            onClick={() => setSelectedCountry(item.country)}
                          />
                        );
                      })}

                      {selectedCountryData && (
                        <InfoWindow
                          position={selectedCountryData.coords}
                          onCloseClick={() => setSelectedCountry(null)}
                        >
                          <div className="min-w-[140px] text-sm text-gray-900">
                            <p className="font-semibold">{selectedCountryData.flag} {selectedCountryData.name}</p>
                            <p>Amount: {CurrencyFormat(selectedCountryData.totalDonationAmount)}</p>
                            <p>Bars: {selectedCountryData.totalBars.toLocaleString()}</p>
                            <p>Donors: {selectedCountryData.totalDonors.toLocaleString()}</p>
                          </div>
                        </InfoWindow>
                      )}
                    </GoogleMap>
                  </div>

                  <div className="max-h-52 overflow-y-auto pr-1">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {mapCountries.map((item) => (
                      <button
                        key={item.country}
                        type="button"
                        onClick={() => setSelectedCountry(item.country)}
                        className="rounded-xl border border-white/60 dark:border-white/10 bg-white/70 dark:bg-slate-900/50 px-3 py-2 text-left hover:bg-white/90 dark:hover:bg-slate-800/80 transition-colors"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{item.flag} {item.name}</p>
                            <p className="text-[11px] text-gray-500 dark:text-gray-400">{item.totalDonors.toLocaleString()} donors</p>
                          </div>
                          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{CurrencyFormat(item.totalDonationAmount)}</p>
                        </div>
                      </button>
                    ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </WidgetWrapper>
  );
}

type MetricCardProps = {
  label: string;
  value: string | number;
  compareValue?: string | number;
  compareLabel: string;
};

function MetricCard({ label, value, compareValue, compareLabel }: MetricCardProps) {
  return (
    <div className="rounded-xl border border-white/60 dark:border-white/10 bg-white/60 dark:bg-slate-900/45 px-3 py-2">
      <p className="text-[10px] uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">{label}</p>
      <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">{value}</p>
      {compareValue !== undefined && (
        <p className="text-[11px] text-gray-500 dark:text-gray-400">{compareLabel}: {compareValue}</p>
      )}
    </div>
  );
}
